import { ChangeEvent, ReactElement, ReactNode, useContext, useState } from "react";
import InputDialog from "../../../../components/dialogs/inputDialog";
import {
  IShiftReportContent,
  IShiftReportEdited,
  IShiftReportMS
} from "./types";
import { cloneObject } from "../../../../system/utils";
import { SystemContext } from "../../../../context/systemContext";
import { Tab, Tabs } from "@mui/material";
import { useSelector } from "react-redux";
import { IState, ISystemContextData } from "../../../../types";
import EAFPanel from "./eafPanel";
import AdditionalIssuesPanel from "./additionalIssuesPanel";
import CasterPanel from "./casterPanel";
import RefractoriesPanel from "./refractoriesPanel";
import LrfPanel from "./lrfPanel";
import MaintenancePanel from "./maintenancePanel";
import CranesPanel from "./cranesPanel";
import SafetyPanel from "./safetyPanel";
import ProductionPanel from "./productionPanel";
import QualityPanel from "./qualityPanel";
import CallOffsPanel from "./callOffsPanel";
import DelaysPanel from "./delaysPanel";
import MaintenanceSAPPanel from "./maintenanceSAPPanel";
import NotesPanel from "./notesPanel";

interface IProps {
  reportData: IShiftReportMS;
  onAccept: (report: IShiftReportEdited) => void;
  onClose: () => void;
}

interface ITabPanelProps {
  children?: ReactNode;
  index: any;
  value: any;
}

const a11yProps = (index: number): any => {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
};

function TabPanel({ children, value, index, ...other }: ITabPanelProps) {

  return (
    <div
      className="flex-grow-1"
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
      style={{ maxHeight: "60vh", overflowY: "auto" }}>
      {value === index && children}
    </div>
  );
}

const processData = (report: IShiftReportMS, systemContext: ISystemContextData): IShiftReportMS => {
  const newObject = cloneObject(report);

  if (!newObject.user && systemContext.user) {
    const user =  systemContext.user;

    newObject.user = {
      userID: user.userID,
      userName: user.fullName,
      email: user.email
    };
  }

  if (!newObject.content) {
    newObject.content = {
      safetyResult: {
        incidents: { value: false, comment: "" },
        prasAndContractingOut: { value: false, comment: "" }
      },
      quality: {
        incidents: { value: false, comment: "" }
      },
      production: {
        heatsDuringShift: { value: 0, comment: "" },
        powerOn: { value: 0, comment: "" },
        powerOff: { value: 0, comment: "" },
        totalProduction: { value: 0, comment: "" },
        chargeWeight: [],
        missedHeats: { value: 0, comment: "" },
        metallicYieldLoss: { value: 0, comment: "" },
        vcbTrips: { value: 0, comment: "" },
      },
      delays: {
        list: [],
        events: [],
      },
      callOffs: {
        incidents: { value: false, comment: "" }
      },
      eaf: {
        bagHouse: { value: false, comment: "" },
        b3Ratio: { value: 0, comment: "" },
        mgO: { value: 0, comment: "" },
        feO: { value: 0, comment: "" }
      },
      lrf: {
        bagHouse: { value: false, comment: "" },
        b3Ratio: { value: 0, comment: "" },
        mgO: { value: 0, comment: "" },
        feO: { value: 0, comment: "" }
      },
      caster: {
        nonFreeOpens: { value: false, comment: "" },
        breakouts: { value: false, comment: "" },
        overflow: { value: false, comment: "" },
        heatsOnTheTundish: { value: 0, comment: "" }
      },
      refractories: {
        tundishes: { value: 0, comment: "" },
        ladleAvailables: { value: 0, comment: "" },
        ladles: []
      },
      cranes: {
        b3: { value: true, comment: "" },
        c1_c2: { value: true, comment: "" },
        e1_e3: { value: true, comment: "" },
        casey_CM: { value: true, comment: "" }
      },
      centralMaintenance: {
        airCompressors: { value: true, comment: "" },
        coolingTower: { value: true, comment: "" },
        hvac: { value: true, comment: "" }
      },
      additionalIssues: [],
      electriciansNotes: [],
      millwrightsNotes: [],
      maintenanceSAP: {
        items: []
      }
    };
  }

  return newObject;
};


const ShiftReportEditor = ({ onClose, reportData, onAccept }: IProps): ReactElement => {
  const systemContext  = useContext(SystemContext);
  const [ editReport, setEditReport ] = useState<IShiftReportMS>(processData(reportData, systemContext));
  const [ edited, setEdited ] = useState(false);
  const darkMode = useSelector((state: IState) => state.darkMode);
  const [tabSelected, setTabSelected] = useState(0);

  const handleChange = (_event: ChangeEvent<any>, newValue: number) => {
    setTabSelected(newValue);
  };

  const onInternalAccept = () => {
    onAccept({
      shiftReportID: editReport.shiftReportID,
      areaID: editReport.area.areaID,
      userID: systemContext.user!.userID,
      zoneID: null,
      shiftHistoryID: editReport.shift.shiftHistoryID,
      content: editReport.content!
    });
  };

  const onChange = (property: keyof IShiftReportContent, value: any) => {
    const newValue = cloneObject<IShiftReportMS>(editReport);

    if (newValue.content) {
      newValue.content[property] = value;
      setEditReport(newValue);
      if (!edited) {
        setEdited(true);
      }
    }
  };

  if (!editReport.content){
    return <></>;
  }

  return (
    <InputDialog onAccept={onInternalAccept} onCancel={onClose} title="Shift Report" open={true} canAccept={edited}>
      <div className="display-flex-column gap-1">
        <Tabs
          value={tabSelected}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
          <Tab label="Safety" {...a11yProps(0)} />
          <Tab label="Quality" {...a11yProps(1)} />
          <Tab label="Production" {...a11yProps(2)} />
          <Tab label="Delays" {...a11yProps(3)} />
          <Tab label="Call Offs" {...a11yProps(4)} />
          <Tab label="EAF" {...a11yProps(5)} />
          <Tab label="LRF" {...a11yProps(6)} />
          <Tab label="Caster" {...a11yProps(7)} />
          <Tab label="Refractories" {...a11yProps(8)} />
          <Tab label="Cranes" {...a11yProps(9)} />
          <Tab label="Central Maintenance" {...a11yProps(10)} />
          <Tab label="Additional Issues" {...a11yProps(11)} />
          <Tab label="Millwrights Notes" {...a11yProps(12)} />
          <Tab label="Electricians Notes" {...a11yProps(13)} />
          <Tab label="SAP Notifications" {...a11yProps(14)} />
        </Tabs>
        <TabPanel value={tabSelected} index={0} >
          <SafetyPanel property="safetyResult" value={editReport.content.safetyResult} onChange={onChange} />
        </TabPanel>
        <TabPanel value={tabSelected} index={1} >
          <QualityPanel property="quality" value={editReport.content.quality} onChange={onChange} />
        </TabPanel>
        <TabPanel value={tabSelected} index={2} >
          <ProductionPanel property="production" value={editReport.content.production} onChange={onChange} shiftHistory={editReport.shift}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={3} >
          <DelaysPanel property="delays" value={editReport.content.delays} onChange={onChange} shiftHistory={editReport.shift} />
        </TabPanel>
        <TabPanel value={tabSelected} index={4} >
          <CallOffsPanel property="callOffs" value={editReport.content.callOffs} onChange={onChange} />
        </TabPanel>
        <TabPanel value={tabSelected} index={5} >
          <EAFPanel property="eaf" value={editReport.content.eaf} onChange={onChange}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={6} >
          <LrfPanel property="lrf" value={editReport.content.lrf} onChange={onChange}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={7} >
          <CasterPanel  property="caster" value={editReport.content.caster} onChange={onChange}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={8} >
          <RefractoriesPanel  property="refractories" value={editReport.content.refractories} onChange={onChange}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={9} >
          <CranesPanel property="cranes" value={editReport.content.cranes} onChange={onChange}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={10} >
          <MaintenancePanel property="centralMaintenance" value={editReport.content.centralMaintenance} onChange={onChange}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={11} >
          <AdditionalIssuesPanel property="additionalIssues" value={editReport.content.additionalIssues} onChange={onChange}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={12} >
          <NotesPanel property="millwrightsNotes" value={editReport.content.millwrightsNotes ?? []} onChange={onChange}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={13} >
          <NotesPanel property="electriciansNotes" value={editReport.content.electriciansNotes ?? []} onChange={onChange}/>
        </TabPanel>
        <TabPanel value={tabSelected} index={14} >
          <MaintenanceSAPPanel property="maintenanceSAP" value={editReport.content.maintenanceSAP} onChange={onChange}/>
        </TabPanel>
      </div>
    </InputDialog>
  );
};


export default ShiftReportEditor;